/**
 * The DOM half of the dialog service: subscription, focus trap, scroll lock, focus restoration.
 *
 * Shared because these are the parts that are easy to get subtly wrong and identical in both
 * apps. What is not shared is the markup, which each app owns.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { getDialogStack, settle, subscribeToDialogs } from './dialogService.js';
import type { DialogRequest } from './types.js';

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useDialogStack(): readonly DialogRequest[] {
  return useSyncExternalStore(subscribeToDialogs, getDialogStack, getDialogStack);
}

/**
 * Wire this to the element rendering the topmost dialog.
 *
 * `active` should be true only for the top of the stack — a trap on a covered dialog fights the
 * one above it for focus, and the user ends up unable to type.
 */
export function useDialogHost(active: boolean, onDismiss: () => void) {
  const containerRef = useRef<HTMLElement | null>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  // External system: document.body's scroll position and the document's focus. Neither is React
  // state, and neither can be expressed as a render.
  useEffect(() => {
    if (!active) return;

    restoreTo.current = document.activeElement as HTMLElement | null;

    const { overflow, paddingRight } = document.body.style;
    // Padding compensates for the disappearing scrollbar, or the whole page jumps sideways as
    // the dialog opens — a small thing that reads as broken.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    const container = containerRef.current;
    const first = container?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? container)?.focus();

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      restoreTo.current?.focus();
    };
  }, [active]);

  // External system: keyboard events on the document, which React cannot deliver to an element
  // that may not have focus yet.
  useEffect(() => {
    if (!active) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss();
        return;
      }

      if (event.key !== 'Tab') return;

      const container = containerRef.current;
      if (!container) return;

      const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (node) => node.offsetParent !== null,
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active, onDismiss]);

  return containerRef;
}

/** Convenience for a renderer: settle the request by id without importing the service directly. */
export function useSettle() {
  return useCallback((id: string, value: boolean | string | null) => {
    settle(id, value);
  }, []);
}
